import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Modal, QRCode, Skeleton, Spin, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {
  createAccountPaySession,
  getAccountComputeBalance,
  getAccountPaymentEntitlement,
  listAccountComputePackages,
  type AccountComputeBalance,
  type AccountPaySession,
  type AccountPaymentEntitlement,
  type AccountPaymentProduct,
} from '../../api';
import { useAppStore } from '../../store';
import { usePaymentOrderPolling } from '../../hooks/usePaymentOrderPolling';
import { buildPaymentUrl } from '../../utils/accountPayment';
import { refreshAccountPaymentData } from '../../utils/accountPaymentRefresh';
import aiPointsIcon from '../../assets/images/ai_points.png';
import memberPayWechatIcon from '../../assets/images/member_pay_wechat.png';

const PAYMENT_QR_EXPIRE_MS = 10 * 60 * 1000;

interface ComputePowerPurchaseModalProps {
  open: boolean;
  onClose: () => void;
  onOpenRules?: () => void;
  onPaymentSuccess?: () => void | Promise<void>;
}

function formatCompute(value?: number | string) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return Math.max(0, numeric).toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatPrice(value?: string | number) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return String(value || '0');
  }
  return numeric.toLocaleString('zh-CN', {
    minimumFractionDigits: numeric % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function formatExpireDate(value?: string | null) {
  if (!value) {
    return '--';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()}`;
}

function formatPaymentQrExpireMinutes() {
  return Math.max(1, Math.ceil(PAYMENT_QR_EXPIRE_MS / 60 / 1000));
}

export const ComputePowerPurchaseModal: React.FC<ComputePowerPurchaseModalProps> = ({
  open,
  onClose,
  onOpenRules,
  onPaymentSuccess,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [creatingSession, setCreatingSession] = useState(false);
  const [packages, setPackages] = useState<AccountPaymentProduct[]>([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [paySession, setPaySession] = useState<AccountPaySession | null>(null);
  const [balance, setBalance] = useState<AccountComputeBalance | null>(null);
  const [entitlement, setEntitlement] = useState<AccountPaymentEntitlement | null>(null);
  const appName = useAppStore((state) => state.appConfig.appName);

  const selectedPackage = useMemo(
    () => packages.find((item) => item.code === selectedCode) || packages[0] || null,
    [packages, selectedCode],
  );
  const paymentUrl = useMemo(() => buildPaymentUrl(paySession), [paySession]);

  const createPaySession = useCallback(async (
    product: AccountPaymentProduct,
    options?: { refreshExpired?: boolean },
  ) => {
    setCreatingSession(true);
    try {
      const payload = await createAccountPaySession({
        planCode: product.code,
        productType: 'COMPUTE_POWER',
      });
      setPaySession(payload);
      if (options?.refreshExpired) {
        messageApi.info('支付二维码已过期，已为你刷新');
      }
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : '创建支付会话失败');
      setPaySession(null);
    } finally {
      setCreatingSession(false);
    }
  }, [messageApi]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setPaySession(null);
    try {
      const [packagePayload, balancePayload, entitlementPayload] = await Promise.all([
        listAccountComputePackages(),
        getAccountComputeBalance().catch(() => null),
        getAccountPaymentEntitlement().catch(() => null),
      ]);
      const nextPackages = packagePayload || [];
      setPackages(nextPackages);
      setBalance(balancePayload || null);
      setEntitlement(entitlementPayload || null);
      const firstPackage = nextPackages[0];
      setSelectedCode(firstPackage?.code || '');
      if (firstPackage) {
        await createPaySession(firstPackage);
      }
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : '算力包信息加载失败');
      setPackages([]);
      setSelectedCode('');
      setPaySession(null);
    } finally {
      setLoading(false);
    }
  }, [createPaySession, messageApi]);

  const handlePaymentPaid = useCallback(async () => {
    const refreshResult = await refreshAccountPaymentData();
    setBalance(refreshResult.computeBalance);
    setEntitlement(refreshResult.entitlement);
    await onPaymentSuccess?.();
    messageApi.success('支付成功');
    onClose();
  }, [messageApi, onClose, onPaymentSuccess]);

  usePaymentOrderPolling({
    open,
    paySession,
    onPaid: handlePaymentPaid,
    onError: (error) => {
      console.warn('算力豆订单状态轮询失败', error);
      messageApi.error(error instanceof Error ? error.message : '支付状态异常，请稍后重试');
    },
  });

  useEffect(() => {
    if (open) {
      void loadData();
    } else {
      setPaySession(null);
      setCreatingSession(false);
    }
  }, [loadData, open]);

  useEffect(() => {
    if (!open || !paySession?.paySessionToken || !selectedPackage) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setPaySession(null);
      void createPaySession(selectedPackage, { refreshExpired: true });
    }, PAYMENT_QR_EXPIRE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [createPaySession, open, paySession?.paySessionToken, selectedPackage]);

  const handlePackageSelect = (product: AccountPaymentProduct) => {
    if (product.code === selectedCode && paySession) {
      return;
    }
    setSelectedCode(product.code);
    setPaySession(null);
    void createPaySession(product);
  };

  const renderPackageCards = () => {
    if (loading) {
      return <Skeleton active paragraph={{ rows: 6 }} />;
    }
    if (!packages.length) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可购买算力包" />;
    }

    return (
      <div className="main-layout__compute-purchase-packages">
        {packages.map((product) => {
          const active = product.code === selectedPackage?.code;
          return (
            <Button
              key={product.code}
              type="text"
              className={`main-layout__compute-purchase-package${active ? ' main-layout__compute-purchase-package--active' : ''}`}
              onClick={() => handlePackageSelect(product)}
            >
              <span className="main-layout__compute-purchase-package-power">
                <img src={aiPointsIcon} alt="" aria-hidden="true" />
                <strong>{formatCompute(product.computePowerAmount)}</strong>
              </span>
              <span className="main-layout__compute-purchase-package-price">
                ¥ {formatPrice(product.amountYuan)}
              </span>
            </Button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        centered
        closable={false}
        footer={null}
        width={665}
        destroyOnHidden
        maskClosable
        onCancel={onClose}
        className="main-layout__compute-purchase-modal"
        styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.45)' } }}
      >
        <div className="main-layout__compute-purchase-shell">
          <div className="main-layout__compute-purchase-profile">
            <div className="main-layout__compute-avatar">N</div>
            <div>
              <strong>{appName}</strong>
              <span>年费会员将于 {formatExpireDate(entitlement?.effectiveUntil)} 过期</span>
            </div>
          </div>
          <Button
            type="text"
            className="main-layout__compute-purchase-close"
            icon={<CloseOutlined />}
            aria-label="关闭算力豆购买弹窗"
            onClick={onClose}
          />
          <div className="main-layout__compute-purchase-balance">
            <span>我的算力豆</span>
            <b>
              <img src={aiPointsIcon} alt="" aria-hidden="true" />
              {formatCompute(balance?.balance)}
            </b>
          </div>
          <section className="main-layout__compute-purchase-panel">
            <div className="main-layout__compute-purchase-title">
              <span />
              <strong>算力豆购买</strong>
              <span />
            </div>
            {renderPackageCards()}
            <div className="main-layout__compute-purchase-pay">
              <span className="main-layout__compute-purchase-pay-title">
                请扫码完成支付
                <img src={memberPayWechatIcon} alt="" aria-hidden="true" />
              </span>
              <div className="main-layout__compute-purchase-qr">
                {creatingSession ? (
                  <Spin />
                ) : paymentUrl ? (
                  <QRCode value={paymentUrl} size={128} bordered={false} />
                ) : (
                  <span>暂无二维码</span>
                )}
              </div>
              <span className="main-layout__payment-qr-expire-tip main-layout__compute-purchase-qr-tip">
                二维码有效期 {formatPaymentQrExpireMinutes()} 分钟，过期后自动刷新
              </span>
            </div>
            <p className="main-layout__compute-purchase-tip">
              温馨提示：算力豆不可兑换会员，不可转赠，也不可提现；<br />
              算力豆充值后有效期为2年，不支持退款或反向兑换为人民币
              <Button
                type="link"
                className="main-layout__compute-rule-link"
                onClick={onOpenRules}
              >
                算力豆规则
              </Button>
            </p>
          </section>
        </div>
      </Modal>
    </>
  );
};
